import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layouts/DashboardLayout';
import { useLanguage } from '../context/LanguageContext';
import { logout, isUserNotFoundError } from '../utils/auth';

// HuPath v2 design tokens
const HT = {
  yellow: '#FFD166', yellowSoft: '#FFE9A8', yellowBg: '#FFF8E7', yellowShadow: '#E0B554',
  green: '#88C057', greenDark: '#5E9E3E', greenSoft: '#D4E8C0', greenBg: '#E8F3D9', greenShield: '#C9E4A5',
  cream: '#FFF8E7',
  coral: '#FF8C42', coralDark: '#E76F51', coralPress: '#B8431E',
  pinkBg: '#FCE4E8', pink: '#F4A6B8',
  brown: '#8B6F47', brownDark: '#6B4F2B',
  text: '#2D2D2D', textSoft: '#6B6B6B', textMuted: '#9A9A9A',
  border: '#E8E0CC', white: '#FFFFFF', teal: '#5AC0B5',
  plum: '#5B2E5E', aqua: '#3DC9B5', accent: '#FFD23F',
};
const FONT_DISPLAY = '"Fredoka", "Baloo 2", "Nunito", sans-serif';
const FONT_BODY = '"Nunito", "Baloo 2", sans-serif';
const SHADOW_SM = '0 2px 6px rgba(139,111,71,0.08)';
const SHADOW_MD = '0 4px 16px rgba(139,111,71,0.10)';

const SettingsPage = () => {
  const navigate = useNavigate();
  const { lang, toggleLang, t } = useLanguage();

  const [userInfo, setUserInfo] = useState({
    fullName: localStorage.getItem('userFullName') || '',
    email: '',
    level: 'Flyers',
    joinedDays: 0,
  });
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;
    fetch(`${process.env.REACT_APP_API_URL}/users/me`, {
      headers: { Authorization: `Bearer ${token}`, accept: 'application/json' },
    })
      .then(async r => {
        if (r.ok) return r.json();
        if (r.status === 404) {
          const errorData = await r.json().catch(() => ({}));
          if (isUserNotFoundError(r, errorData)) logout(navigate);
        }
        return null;
      })
      .then(data => {
        if (data) {
          const name = data.full_name || data.email || userInfo.fullName;
          localStorage.setItem('userFullName', name);
          setUserInfo(prev => ({ ...prev, fullName: name, email: data.email || '' }));
        }
      })
      .catch(() => { });
  }, [navigate, userInfo.fullName]);

  const handleLogout = () => logout(navigate);
  const displayName = userInfo.fullName || userInfo.email || t('user');

  return (
    <DashboardLayout showBottomNav={false}>
      <div style={{
        flex: 1, background: HT.yellowBg, fontFamily: FONT_BODY, color: HT.text,
        padding: '24px 16px 48px',
      }}>
        <div style={{ maxWidth: 900, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 22 }}>

          {/* Header card */}
          <div style={{
            background: HT.white,
            border: `1px solid ${HT.border}`, boxShadow: SHADOW_MD,
            borderRadius: 16, padding: '22px 24px',
            display: 'flex', flexDirection: 'column', gap: 16,
          }}
          className="md:!flex-row md:!items-center md:!gap-6"
          >
            <div style={{ position: 'relative', flexShrink: 0, alignSelf: 'center' }}>
              <div style={{
                width: 100, height: 100, borderRadius: 24,
                background: HT.yellowSoft,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <span className="material-symbols-outlined" style={{ fontSize: 60, color: HT.brown, fontVariationSettings: "'FILL' 1" }}>
                  account_circle
                </span>
              </div>
              <div style={{
                position: 'absolute', bottom: -6, right: -6,
                width: 36, height: 36, borderRadius: '50%',
                background: HT.teal,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: SHADOW_SM,
              }}>
                <span className="material-symbols-outlined" style={{ fontSize: 20, color: HT.white, fontVariationSettings: "'FILL' 1" }}>verified</span>
              </div>
            </div>

            <div style={{ flex: 1, textAlign: 'center' }} className="md:!text-left">
              <h1 style={{ fontSize: 28, fontFamily: FONT_DISPLAY, fontWeight: 600, color: HT.text, margin: 0, letterSpacing: -0.6, lineHeight: 1.15 }}>
                {displayName}
              </h1>
              {userInfo.email && (
                <p style={{ fontSize: 13, fontWeight: 600, color: HT.textSoft, margin: '4px 0 10px' }}>
                  {userInfo.email}
                </p>
              )}
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                background: HT.coralDark, color: HT.white,
                padding: '6px 14px', borderRadius: 999,
                fontSize: 12, fontFamily: FONT_DISPLAY, fontWeight: 600, letterSpacing: 1.2, textTransform: 'uppercase',
              }}>
                <span className="material-symbols-outlined" style={{ fontSize: 16, fontVariationSettings: "'FILL' 1" }}>flight_takeoff</span>
                {t('levelLabel')}: {userInfo.level}
              </div>
            </div>
          </div>

          {/* Settings grid */}
          <div style={{
            display: 'grid', gap: 18,
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          }}>
            {/* General section */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ fontSize: 11, fontFamily: FONT_DISPLAY, fontWeight: 600, color: HT.textMuted, letterSpacing: 2.5, textTransform: 'uppercase', paddingLeft: 4 }}>
                {t('generalSection')}
              </div>
              <div style={{
                background: HT.white,
                border: `1px solid ${HT.border}`, boxShadow: SHADOW_SM,
                borderRadius: 16, padding: 14,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                    <div style={{
                      width: 44, height: 44, borderRadius: 12,
                      background: HT.greenBg,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <span className="material-symbols-outlined" style={{ fontSize: 22, color: HT.greenDark }}>translate</span>
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontFamily: FONT_DISPLAY, fontWeight: 600, color: HT.text }}>
                        {t('languageLabel')}
                      </div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: HT.textMuted }}>
                        {t('languageSub')}
                      </div>
                    </div>
                  </div>
                  <div style={{
                    display: 'flex', gap: 4,
                    background: HT.yellowBg, padding: 4,
                    border: `1px solid ${HT.border}`, borderRadius: 12,
                    flexShrink: 0,
                  }}>
                    {['vi', 'en'].map((l) => {
                      const active = lang === l;
                      return (
                        <button
                          key={l}
                          onClick={() => lang !== l && toggleLang()}
                          style={{
                            padding: '5px 12px', borderRadius: 8,
                            border: active ? `1px solid ${HT.yellowShadow}` : '1px solid transparent',
                            background: active ? HT.yellow : 'transparent',
                            boxShadow: active ? SHADOW_SM : 'none',
                            fontFamily: FONT_BODY, fontSize: 11, fontWeight: 700,
                            color: active ? HT.brownDark : HT.textSoft,
                            cursor: active ? 'default' : 'pointer',
                            letterSpacing: 0.5,
                          }}
                        >{l.toUpperCase()}</button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Account section */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ fontSize: 11, fontFamily: FONT_DISPLAY, fontWeight: 600, color: HT.textMuted, letterSpacing: 2.5, textTransform: 'uppercase', paddingLeft: 4 }}>
                {t('accountSection')}
              </div>
              <button
                onClick={() => setShowLogoutConfirm(true)}
                style={{
                  background: HT.white,
                  border: `1px solid ${HT.border}`, boxShadow: SHADOW_SM,
                  borderRadius: 16, padding: 14,
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                  cursor: 'pointer', textAlign: 'left', width: '100%',
                  fontFamily: FONT_BODY,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: 12,
                    background: HT.pinkBg,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 22, color: HT.coralDark }}>logout</span>
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontFamily: FONT_DISPLAY, fontWeight: 600, color: HT.text }}>
                      {t('logout')}
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: HT.textMuted }}>
                      {t('logoutSub')}
                    </div>
                  </div>
                </div>
                <span className="material-symbols-outlined" style={{ fontSize: 22, color: HT.textMuted }}>chevron_right</span>
              </button>
            </div>
          </div>

          {/* Footer */}
          <div style={{ marginTop: 26, textAlign: 'center', opacity: 0.5 }}>
            <div style={{ fontSize: 11, fontFamily: FONT_DISPLAY, fontWeight: 600, color: HT.textSoft, letterSpacing: 3, textTransform: 'uppercase' }}>
              HuPath Flyers Prep
            </div>
            <div style={{ fontSize: 10, fontWeight: 700, color: HT.textMuted, marginTop: 4 }}>
              {t('version')} 1.0.24
            </div>
          </div>
        </div>
      </div>

      {/* Logout Modal */}
      {showLogoutConfirm && (
        <div
          className="animate-fadeIn"
          style={{
            position: 'fixed', inset: 0, zIndex: 100,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 20, fontFamily: FONT_BODY, color: HT.text,
          }}
        >
          <div
            onClick={() => setShowLogoutConfirm(false)}
            style={{ position: 'absolute', inset: 0, background: 'rgba(45,45,45,0.4)', backdropFilter: 'blur(6px)' }}
          />
          <div
            className="animate-scaleIn"
            style={{
              position: 'relative', maxWidth: 420, width: '100%',
              background: HT.white,
              border: `1px solid ${HT.border}`, boxShadow: SHADOW_MD,
              borderRadius: 20, padding: '28px 26px', textAlign: 'center',
            }}
          >
            <div style={{
              width: 80, height: 80, borderRadius: 20,
              background: HT.pinkBg,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 18px',
            }}>
              <span className="material-symbols-outlined" style={{ fontSize: 44, color: HT.coralDark, fontVariationSettings: "'FILL' 1" }}>
                door_open
              </span>
            </div>
            <h2 style={{ fontSize: 24, fontFamily: FONT_DISPLAY, fontWeight: 600, color: HT.text, margin: '0 0 8px', letterSpacing: -0.5 }}>
              {t('confirmLogoutTitle')}
            </h2>
            <p style={{ fontSize: 14, fontWeight: 600, color: HT.textSoft, margin: '0 0 22px', lineHeight: 1.4 }}>
              {t('confirmLogoutMsg')}
            </p>

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => setShowLogoutConfirm(false)}
                style={{
                  flex: 1, padding: '12px 18px', borderRadius: 999,
                  border: `1px solid ${HT.border}`,
                  background: HT.white, color: HT.text,
                  boxShadow: SHADOW_SM,
                  fontFamily: FONT_DISPLAY, fontSize: 14, fontWeight: 600,
                  cursor: 'pointer',
                }}
              >{t('cancel')}</button>
              <button
                onClick={handleLogout}
                style={{
                  flex: 1, padding: '12px 18px', borderRadius: 999,
                  border: 'none',
                  background: HT.coralDark, color: HT.white,
                  boxShadow: SHADOW_SM,
                  fontFamily: FONT_DISPLAY, fontSize: 14, fontWeight: 600,
                  cursor: 'pointer', letterSpacing: 0.3,
                }}
              >{t('logout')}</button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.92) translateY(12px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        .animate-fadeIn { animation: fadeIn 0.2s ease-out; }
        .animate-scaleIn { animation: scaleIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) forwards; }
      `}</style>
    </DashboardLayout>
  );
};

export default SettingsPage;
